// Location Service for IPWho API integration
// This service handles geolocation detection and provides utilities for location-based features

import type { IPWhoResponse } from "@/types/ipwho";

// Create a default empty response for error cases
const createEmptyResponse = (error?: string): IPWhoResponse => ({
  success: false,
  error
});

class LocationService {
  private static instance: LocationService;
  private cachedLocation: IPWhoResponse | null = null;
  private locationPromise: Promise<IPWhoResponse> | null = null;

  private constructor() {}

  static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  // Function to get IP geolocation data for specific IP
  async getIPLocation(ipAddress: string): Promise<IPWhoResponse> {
    try {
      const response = await fetch(`https://api.ipwho.org/ip/${ipAddress}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: IPWhoResponse = await response.json();
      
      
      if (data.success && data.data) {
        const location = data.data;
        console.log(`${location.flag.flag_Icon} Country: ${location.country} (${location.countryCode})`);
      } else {
        console.error('❌ Failed to get location data:', data);
      }
      
      return data;
    } catch (error) {
      console.error('🚨 Error fetching IP location:', error);
      return createEmptyResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  // Function to get user's own IP location with caching
  async getMyLocation(): Promise<IPWhoResponse> {
    // Return cached result if available
    if (this.cachedLocation) {
      return this.cachedLocation;
    }

    // Return existing promise if one is in progress
    if (this.locationPromise) {
      return this.locationPromise;
    }

    // Create new promise and cache it
    this.locationPromise = this.fetchMyLocation();
    
    try {
      const result = await this.locationPromise;
      this.cachedLocation = result;
      return result;
    } finally {
      this.locationPromise = null;
    }
  }

  private async fetchMyLocation(): Promise<IPWhoResponse> {
    try {
      const response = await fetch('https://api.ipwho.org/me');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: IPWhoResponse = await response.json();
      
      return data;
    } catch (error) {
      console.error('🚨 Error fetching your IP location:', error);
      return createEmptyResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  // Utility function to check if user is in California
  isUserInCalifornia(): boolean {
    if (!this.cachedLocation || this.cachedLocation.error || !this.cachedLocation.success || !this.cachedLocation.data) {
      return false;
    }

    const { region, regionCode, country, countryCode } = this.cachedLocation.data;
    
    // Check if user is in California, USA
    const isUSA = country === 'United States' || countryCode === 'US';
    const isCalifornia = region === 'California' || regionCode === 'CA';
    
    return isUSA && isCalifornia;
  }

  // Clear cached location (useful for testing or manual refresh)
  clearCache(): void {
    this.cachedLocation = null;
    this.locationPromise = null;
  }

  // Get cached location without making API call
  getCachedLocation(): IPWhoResponse | null {
    return this.cachedLocation;
  }
}

// Export singleton instance
export const locationService = LocationService.getInstance();

// Export utility functions for easy use
export const getMyLocation = () => locationService.getMyLocation();
export const getIPLocation = (ip: string) => locationService.getIPLocation(ip);
export const isUserInCalifornia = () => locationService.isUserInCalifornia();
export const clearLocationCache = () => locationService.clearCache();
